import { useEffect, useState } from 'react';

import { Save } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControlLabel,
  Paper,
  Skeleton,
  Switch,
  Typography,
} from '@mui/material';
import { FormikProvider, useFormik } from 'formik';
import { isEqual, pick } from 'lodash';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useDispatch } from 'react-redux';
import { Link, useHistory, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import FullScreenLoader from 'src/components/FullscreenLoading';
import MetaTags from 'src/components/Seo/MetaTags';
import {
  bulkCreateUpdateOrDeleteAlertIndicatorConfigurations,
  getAlertIndicatorsByWalletAndFundId,
  getIndicatorsColumns,
  getOwnerDefaultIndicatorsAlertConfigurations,
  getWalletById,
  getWalletFundById,
  updateWalletFundUseAlertDefaultData,
} from 'src/services/wallet';
import { FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX } from 'src/services/wallet/queryKeys';
import { setBannerMessage } from 'src/store/display';
import theme from 'src/theme';
import {
  AlertConfiguration,
  IndicatorAlertConfiguration,
  IndicatorAlertCriticalityEnum,
  IndicatorAlertType,
  IndicatorWithAlerts,
} from 'src/types/indicator-alert-configuration';
import { WalletFund } from 'src/types/wallet';
import { ScrollToTop } from 'src/utils/scroll';
import { truncateText } from 'src/utils/string';

import AlertForm from './components/AlertForm';
import { useAlertContext } from './context/AlertContextProvider';
import { AlertContextProvider } from './context/AlertContextProvider';
import { prepareAlertsToPersist } from './utils/alertUtils';
import { formikAlertValidationSchema } from './validation/alertValidation';
import { useMonitorContext } from '../../MonitorContextProvider';
import TabNavigation from '../../components/TabNavigation';
import { DynamicColumn } from '../components/IndicatorsTable';

const percentageColumnsOrdered = [
  'delta_dc_pct',
  'delta_atrasos_pct',
  'delta_pdd_pct',
  'delta_recompras_pct',
  'delta_substituicoes_pct',
  'pct_atraso_sobre_dc',
  'pdd_sobre_dc',
  'pct_pdd_sobre_atrasos',
  'recompra_sobre_dc',
  'amort_pct',
  'pct_caixa_sobre_pl',
];

function AlertFundsContent() {
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const { walletId, fundId } = useParams<{
    walletId: string;
    fundId: string;
  }>();
  const history = useHistory();
  const {
    setBreadcrumbsItens,
    wallet,
    setWallet,
    defaultUserAlertConfigurations,
    setDefaultUserAlertConfigurations,
  } = useMonitorContext();

  const {
    percentageAlerts,
    amountAlerts,
    useAlertDefaultData,
    setPercentageAlerts,
    setAmountAlerts,
    addAlert,
    updateAlert,
    removeAlert,
    initializeAlerts,
    getAlertsByTab,
    getAlertsToDelete,
    setUseAlertDefaultData,
  } = useAlertContext();

  const [selectedFund, setSelectedFund] = useState<WalletFund | null>(null);
  const [fundIndicators, setFundIndicators] =
    useState<Array<DynamicColumn> | null>(null);
  const [selectedTab, setSelectedTab] = useState<'percentage' | 'amount'>(
    'percentage',
  );
  const [deleteConfirmation, setDeleteConfirmation] = useState<{
    isOpen: boolean;
    indicatorKey: string;
    internalId: string;
    isModified: boolean;
    id: number | null;
  }>({
    isOpen: false,
    indicatorKey: '',
    internalId: '',
    isModified: false,
    id: null,
  });

  const [alertIndicatorsData, setAlertIndicatorsData] =
    useState<Array<IndicatorAlertConfiguration> | null>(null);

  const handleTabChange = (newTab: 'percentage' | 'amount') => {
    if (formik.values.indicatorsWithAlerts?.length > 0) {
      if (selectedTab === 'percentage') {
        setPercentageAlerts(formik.values.indicatorsWithAlerts);
      } else {
        setAmountAlerts(formik.values.indicatorsWithAlerts);
      }
    }

    setSelectedTab(newTab);

    const newTabData =
      newTab === 'percentage' ? percentageAlerts : amountAlerts;

    if (newTabData.length > 0) {
      formik.setFieldValue('indicatorsWithAlerts', newTabData);
    } else {
      formik.setFieldValue('indicatorsWithAlerts', []);
    }
  };

  const { isLoading: isLoadingWallet, refetch: refetchWallet } = useQuery({
    queryKey: ['wallets', walletId],
    queryFn: () => getWalletById(walletId),
    onSuccess: (data) => {
      setWallet(data);
    },
    onError: () => {
      toast.error('Erro ao buscar carteira');
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const { isLoading: isLoadingWalletFund } = useQuery({
    queryKey: ['wallet-fund', fundId],
    queryFn: () => getWalletFundById(fundId),
    onSuccess: (data) => {
      if (data && data.length > 0) {
        setSelectedFund(data ? data[0] : null);
        setUseAlertDefaultData(data ? data[0].useAlertDefault : true);
      } else {
        history.replace('/monitor');
        dispatch(
          setBannerMessage({
            message:
              'O recurso que você está tentando acessar não está mais disponível.',
            type: 'error',
            autoclose: true,
          }),
        );
      }
    },
    enabled: !!fundId,
  });

  const { isLoading: isLoadingIndicators } = useQuery({
    queryKey: ['indicators', walletId, selectedTab, fundId],
    queryFn: () => getIndicatorsColumns(Number(walletId), selectedTab),
    onSuccess: (data) => {
      setFundIndicators((prev) => [...data?.data]);
    },
    enabled: !!walletId && !!selectedTab && !!fundId,
  });

  const {
    // data: alertIndicatorsData,
    isLoading: isLoadingAlertIndicators,
    refetch: refetchAlertIndicators,
  } = useQuery({
    queryKey: ['alert-indicators', walletId, fundId, useAlertDefaultData],
    queryFn: () => {
      if (useAlertDefaultData) {
        return getOwnerDefaultIndicatorsAlertConfigurations();
      }

      if (!useAlertDefaultData && !!walletId && !!fundId) {
        return getAlertIndicatorsByWalletAndFundId(
          Number(walletId),
          Number(fundId),
        );
      }

      return Promise.resolve([]);
    },
    onSuccess: (data: IndicatorAlertConfiguration[]) => {
      setAlertIndicatorsData(data);
    },
    enabled:
      !!fundId &&
      (useAlertDefaultData || (!useAlertDefaultData && !!walletId && !!fundId)),
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: true,
  });

  const createNewAlert = (indicatorKey: string): AlertConfiguration => {
    return {
      id: null,
      internalId: `temp_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`,
      type: IndicatorAlertType.FUND,
      entityId: Number(fundId),
      indicator: indicatorKey,
      startValue: 0,
      endValue: undefined,
      operator: 'gt',
      criticality: IndicatorAlertCriticalityEnum.ALERT,
      isActive: false,
      email: false,
      alertConfiguration: undefined,
      tabType: selectedTab,
    };
  };

  const handleAddAlert = (indicatorKey: string) => {
    const newAlert = createNewAlert(indicatorKey);

    const currentAlerts = formik.values.indicatorsWithAlerts || [];
    const updatedAlerts = currentAlerts.map((indicator) =>
      indicator.key === indicatorKey
        ? { ...indicator, alerts: [...indicator.alerts, newAlert] }
        : indicator,
    );

    formik.setFieldValue('indicatorsWithAlerts', updatedAlerts);

    addAlert(selectedTab, newAlert, indicatorKey);
  };

  const handleUpdateAlert = (
    indicatorKey: string,
    internalId: string,
    field: string,
    value: any,
  ) => {
    const updatedAlerts = formik.values.indicatorsWithAlerts.map((indicator) =>
      indicator.key === indicatorKey
        ? {
            ...indicator,
            alerts: indicator.alerts.map((alert) =>
              alert.internalId === internalId
                ? { ...alert, [field]: value }
                : alert,
            ),
          }
        : indicator,
    );

    formik.setFieldValue('indicatorsWithAlerts', updatedAlerts);

    updateAlert(selectedTab, internalId, { [field]: value }, indicatorKey);
  };

  const handleDeleteConfirmation = (
    indicatorKey: string,
    internalId: string,
    id: number | null,
  ) => {
    setDeleteConfirmation({
      isOpen: true,
      indicatorKey,
      internalId,
      isModified: id !== null,
      id,
    });
  };

  const handleConfirmDelete = () => {
    const { indicatorKey, internalId, id } = deleteConfirmation;
    handleRemoveAlert(indicatorKey, internalId, id);

    setDeleteConfirmation({
      isOpen: false,
      indicatorKey: '',
      internalId: '',
      isModified: false,
      id: null,
    });
  };

  const handleCancelDelete = () => {
    setDeleteConfirmation({
      isOpen: false,
      indicatorKey: '',
      internalId: '',
      isModified: false,
      id: null,
    });
  };

  const handleRemoveAlert = (
    indicatorKey: string,
    internalId: string,
    id: number | null,
  ) => {
    const currentAlerts = formik.values.indicatorsWithAlerts || [];

    const alertToDelete = currentAlerts
      .find((indicator) => indicator.key === indicatorKey)
      ?.alerts.find((alert) => alert.internalId === internalId);

    const updatedAlerts = currentAlerts.map((indicator) =>
      indicator.key === indicatorKey
        ? {
            ...indicator,
            alerts: indicator.alerts.filter(
              (alert) => alert.internalId !== internalId,
            ),
          }
        : indicator,
    );

    formik.setFieldValue('indicatorsWithAlerts', updatedAlerts);

    removeAlert(
      selectedTab,
      internalId,
      indicatorKey,
      id !== null ? id : alertToDelete?.id || null,
    );
  };

  const mapToAlertConfiguration = (
    alert: IndicatorAlertConfiguration,
    valueType: 'percentage' | 'amount',
  ): AlertConfiguration => {
    const result = {
      id: alert.id,
      internalId: `alert_${alert.id}`,
      type: IndicatorAlertType.FUND,
      entityId: Number(alert.entityId),
      indicator: alert.indicator.column,
      startValue: alert.criteriaStartValue,
      endValue: alert.criteriaEndValue || undefined,
      operator: alert.criteriaOperator,
      criticality: alert.criticality,
      isActive: alert.isActive,
      email: alert.email || false,
      alertConfiguration: alert.alertConfigurationId || undefined,
      tabType: valueType,
    };

    return result;
  };

  const formatExistingAlerts = (
    alerts: IndicatorAlertConfiguration[],
    indicators: Array<DynamicColumn> | null,
  ): IndicatorWithAlerts[] => {
    if (!indicators) return [];

    const alertsByIndicator = new Map<string, IndicatorAlertConfiguration[]>();

    alerts.forEach((alert) => {
      const key = alert.indicator?.column;
      if (key) {
        const existing = alertsByIndicator.get(key) || [];
        alertsByIndicator.set(key, [...existing, alert]);
      }
    });

    const mappedIndicators = indicators.map((indicator) => ({
      key: indicator.accessorKey,
      label: indicator.header,
      valueType: selectedTab,
      alerts: (alertsByIndicator.get(indicator.accessorKey) || []).map(
        (alert) => mapToAlertConfiguration(alert, selectedTab),
      ),
    }));

    return selectedTab === 'percentage'
      ? mappedIndicators.sort((a, b) => {
          const indexA = percentageColumnsOrdered.indexOf(a.key);
          const indexB = percentageColumnsOrdered.indexOf(b.key);

          if (indexA !== -1 && indexB !== -1) {
            return indexA - indexB;
          }

          if (indexA !== -1 && indexB === -1) {
            return -1;
          }

          if (indexA === -1 && indexB !== -1) {
            return 1;
          }

          return 0;
        })
      : mappedIndicators;
  };

  const {
    mutate: mutateBulkCreateUpdateOrDeleteAlertIndicatorConfigurations,
    isLoading: isLoadingSave,
  } = useMutation(bulkCreateUpdateOrDeleteAlertIndicatorConfigurations, {
    onSuccess: () => {
      initializeAlerts([], [], []);

      formik.resetForm({
        values: {
          indicatorsWithAlerts: [],
        },
      });

      dispatch(
        setBannerMessage({
          message: 'Configuração de Alertas de Indicadores salva com sucesso!',
          type: 'success',
          autoclose: true,
        }),
      );

      refetchAlertIndicators();
      refetchWallet();
      queryClient.invalidateQueries(['wallet-warning-summary', walletId]);
      queryClient.invalidateQueries(
        FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX,
      );
    },
    onError: () => {
      dispatch(
        setBannerMessage({
          message: 'Erro ao processar alertas. Tente novamente.',
          type: 'error',
          autoclose: true,
        }),
      );
    },
  });

  const {
    mutate: updateWalletFundDataMutation,
    isLoading: isLoadingUpdateWalletFundData,
  } = useMutation(updateWalletFundUseAlertDefaultData, {
    onSuccess: ({ data }) => {
      setUseAlertDefaultData(data.useAlertDefault);
      queryClient.invalidateQueries(['wallet-warning-summary', walletId]);
      queryClient.invalidateQueries(
        FOLLOWING_FIC_DASHBOARD_SUMMARY_QUERY_PREFIX,
      );
    },
  });

  useEffect(() => {
    const deletedAlerts = getAlertsToDelete();

    if (fundIndicators && alertIndicatorsData && fundId) {
      const formattedAlerts = formatExistingAlerts(
        alertIndicatorsData,
        fundIndicators,
      );

      const filteredAlerts = formattedAlerts.map((indicator) => ({
        ...indicator,
        alerts: indicator.alerts.filter((alert) => {
          if (alert.id !== null && deletedAlerts.includes(alert.id)) {
            return false;
          }
          return true;
        }),
      }));

      if (selectedTab === 'percentage' && percentageAlerts.length === 0) {
        setPercentageAlerts(filteredAlerts);
      } else if (selectedTab === 'amount' && amountAlerts.length === 0) {
        setAmountAlerts(filteredAlerts);
      }

      if (formik.values.indicatorsWithAlerts?.length > 0) {
        const mergedAlerts = filteredAlerts.map((backendIndicator) => {
          const existingIndicator = formik.values.indicatorsWithAlerts.find(
            (existing) => existing.key === backendIndicator.key,
          );

          if (existingIndicator) {
            const tempAlerts = existingIndicator.alerts.filter(
              (alert) => alert.id === null,
            );

            const modifiedAlerts = existingIndicator.alerts.filter((alert) => {
              if (alert.id === null) return false;

              const backendAlert = backendIndicator.alerts.find(
                (backendAlert) => backendAlert.id === alert.id,
              );

              if (!backendAlert) return false;

              const fieldsToCompare = [
                'startValue',
                'endValue',
                'operator',
                'criticality',
                'isActive',
                'email',
              ];
              const isModified = !isEqual(
                pick(alert, fieldsToCompare),
                pick(backendAlert, fieldsToCompare),
              );

              return isModified;
            });

            let allAlerts = [...backendIndicator.alerts];

            modifiedAlerts.forEach((modifiedAlert) => {
              const backendIndex = allAlerts.findIndex(
                (alert) => alert.id === modifiedAlert.id,
              );

              if (backendIndex !== -1) {
                allAlerts[backendIndex] = modifiedAlert;
              }
            });

            allAlerts = [...allAlerts, ...tempAlerts];

            return {
              ...backendIndicator,
              alerts: allAlerts,
            };
          }

          return backendIndicator;
        });

        formik.setFieldValue('indicatorsWithAlerts', mergedAlerts);
      } else {
        formik.setFieldValue('indicatorsWithAlerts', filteredAlerts);
      }
    }
  }, [fundIndicators, alertIndicatorsData, fundId]);

  useEffect(() => {
    if (wallet) {
      setBreadcrumbsItens((prev) => [
        ...prev,
        {
          title: `Carteira - ${truncateText(wallet?.name, 25)}`,
          link: `/monitor/wallet/${walletId}`,
        },
        `Alertas do Fundo - ${selectedFund?.operation?.name || 'DESCONHECIDO'}`,
      ]);

      return () => {
        setBreadcrumbsItens((prev) => prev.slice(0, -2));
      };
    }

    if (!walletId) {
      history.push('/monitor');
    }

    if (walletId && !wallet) {
      refetchWallet();
    }
  }, [
    walletId,
    fundId,
    wallet,
    history,
    refetchWallet,
    selectedFund?.operation?.name,
  ]);

  useEffect(() => {
    refetchAlertIndicators();
  }, [useAlertDefaultData]);

  const formik = useFormik<{ indicatorsWithAlerts: IndicatorWithAlerts[] }>({
    validationSchema: formikAlertValidationSchema,
    initialValues: {
      indicatorsWithAlerts: [],
    },
    enableReinitialize: true,
    validateOnMount: false,
    onSubmit: () => {
      const indPercentageAlerts = getAlertsByTab('percentage');
      const indAmountAlerts = getAlertsByTab('amount');
      const alertsIdsToDelete = getAlertsToDelete();

      const toPersist = prepareAlertsToPersist(walletId, fundId, {
        percentageAlerts: indPercentageAlerts.flatMap(
          (indicator) => indicator.alerts,
        ),
        amountAlerts: indAmountAlerts.flatMap((indicator) => indicator.alerts),
        alertsIdsToDelete,
      });

      mutateBulkCreateUpdateOrDeleteAlertIndicatorConfigurations(toPersist);
    },
  });

  if (isLoadingWalletFund || isLoadingIndicators || isLoadingAlertIndicators) {
    return (
      <ScrollToTop>
        <Box sx={{ p: 2 }}>
          <MetaTags
            title="Alertas do Fundo"
            description="Configuração de alertas para indicadores do fundo"
          />
          <Paper
            elevation={2}
            sx={{
              p: 1,
              borderRadius: '5px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="center"
              p={4}>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={400}
                animation="wave"
              />
            </Box>
          </Paper>
        </Box>
      </ScrollToTop>
    );
  }

  const isLoading = isLoadingIndicators || isLoadingAlertIndicators;

  return (
    <ScrollToTop>
      <FullScreenLoader
        open={isLoadingSave || isLoadingUpdateWalletFundData}
        message="Aguarde..."
      />
      <Box sx={{ p: 2 }}>
        <MetaTags
          title="Alertas do Fundo"
          description="Configuração de alertas para indicadores do fundo"
        />
        <FormikProvider value={formik}>
          <Paper
            elevation={2}
            sx={{
              p: 1,
              borderRadius: '5px',
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}>
            {/* Header da página */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              p={2}>
              <Box
                display="column"
                alignItems="left"
                justifyContent="left"
                gap={2}
                sx={{
                  maxWidth: '62%',
                }}>
                <Typography variant="h5">Cadastro de Alertas</Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '70%',
                    cursor: 'pointer',
                  }}
                  title={wallet?.name?.toUpperCase()}>
                  Carteira: {wallet?.name?.toUpperCase()}{' '}
                </Typography>
                <Typography
                  variant="subtitle1"
                  sx={{
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    width: '70%',
                    cursor: 'pointer',
                  }}
                  title={selectedFund?.operation?.name || ''}>
                  Fundo: {selectedFund?.operation?.name || ''}
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={3}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={useAlertDefaultData}
                      onChange={() =>
                        updateWalletFundDataMutation({
                          walletFundId: selectedFund?.id || 0,
                          walletDefaultAlertData: !useAlertDefaultData,
                        })
                      }
                      size="small"
                    />
                  }
                  labelPlacement="start"
                  label="Usar Alertas Padrão do Usuário"
                  slotProps={{
                    typography: {
                      fontSize: '14px',
                    },
                  }}
                />
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => formik.handleSubmit()}
                  disabled={isLoadingSave || useAlertDefaultData}
                  title="Salvar Alertas"
                  size="small"
                  startIcon={<Save />}>
                  Salvar Alertas
                </Button>
              </Box>
            </Box>

            {/* Mensagem de utilização de alertas padrão do usuário e seu redirecionamento para a página de alertas padrão do usuário*/}
            {useAlertDefaultData && (
              <Alert
                severity="info"
                sx={{ mx: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2">
                  Você está utilizando as Configurações de Alertas de
                  Indicadores do Padrão do usuário. &nbsp;
                  <Link to="/monitor/alerts">Clique aqui</Link>
                  &nbsp; para gerenciá-los.
                </Typography>
              </Alert>
            )}

            {/* Abas Porcentagem/Montante */}
            <TabNavigation
              styles={{
                width: '100%',
                px: 4,
                pt: 0,
                mt: 0,
              }}
              value={selectedTab}
              handleSelectTab={handleTabChange}
            />

            {/* Lista de indicadores com alertas */}
            <Box sx={{ px: 1, pb: 4 }}>
              {formik.values.indicatorsWithAlerts?.length > 0 && !isLoading ? (
                <Box component="form">
                  <fieldset
                    disabled={useAlertDefaultData}
                    style={{
                      border: 'none',
                      padding: 0,
                      margin: 0,
                    }}>
                    {/* Tabela única com todos os indicadores */}
                    <AlertForm
                      indicatorsWithAlerts={formik.values.indicatorsWithAlerts}
                      onAddAlert={handleAddAlert}
                      onUpdateAlert={handleUpdateAlert}
                      onRemoveAlert={handleDeleteConfirmation}
                    />
                  </fieldset>
                </Box>
              ) : (
                <Box p={4} textAlign="center">
                  <Typography variant="body1" color="text.secondary">
                    {isLoading
                      ? 'Carregando indicadores...'
                      : 'Nenhum indicador encontrado para este fundo.'}
                  </Typography>
                </Box>
              )}
            </Box>
          </Paper>
        </FormikProvider>
      </Box>
      <Dialog
        open={deleteConfirmation.isOpen}
        onClose={handleCancelDelete}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description">
        <DialogTitle id="alert-dialog-title">
          Confirmar Remoção de Alertas
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            <Typography variant="body1">
              Você tem certeza que deseja remover este alerta?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação não poderá ser desfeita caso o botão{' '}
              <strong
                style={{
                  justifyContent: 'center',
                  alignItems: 'center',
                  color: theme.palette.secondary.main,
                }}>
                Salvar Alertas
              </strong>
              &nbsp; seja clicado!
            </Typography>
          </DialogContentText>

          {/* Aviso especial para alertas modificados */}
          {deleteConfirmation.isModified && (
            <Box mt={2} p={2} bgcolor="error.main" borderRadius={1}>
              <Typography variant="body2" color="white" fontWeight="bold">
                ⚠️ ATENÇÃO: Este alerta foi modificado e foi solicitado sua
                remoção!
              </Typography>
              <Typography variant="body2" color="white" mt={1}>
                Você clicou para remover um alerta que foi modificado! <br />
                Se prosseguir, as alterações serão descartadas e o alerta será
                removido ao clicar no botão 'Salvar Alertas'.
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} color="primary">
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmDelete}
            color="primary"
            variant="contained">
            Remover Alerta
          </Button>
        </DialogActions>
      </Dialog>
    </ScrollToTop>
  );
}

export default function AlertFunds() {
  return (
    <AlertContextProvider>
      <AlertFundsContent />
    </AlertContextProvider>
  );
}
